import {
  BookOpenCheck,
  Camera,
  ChevronLeft,
  ChevronRight,
  Heart,
  LayoutDashboard,
  Loader2,
  Share2,
  SlidersHorizontal,
  type LucideIcon
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { Dialog, DialogContent } from './ui/Dialog';

interface GuideStep {
  title: string;
  eyebrow: string;
  description: string;
  detail: string;
  icon: LucideIcon;
}

const GUIDE_STEPS: GuideStep[] = [
  {
    title: 'Create and manage galleries',
    eyebrow: 'Your dashboard',
    description: 'Choose New gallery to create a private client gallery and set its first passcode.',
    detail: 'Manage Galleries lets you make several galleries inactive or permanently remove them.',
    icon: LayoutDashboard
  },
  {
    title: 'Upload and organize media',
    eyebrow: 'Photos & Media',
    description: 'Open a gallery and choose Add Media to upload photos or supported videos.',
    detail: 'Use Scenes to group photos in the order clients should browse them.',
    icon: Camera
  },
  {
    title: 'Prepare the client experience',
    eyebrow: 'Gallery Setup',
    description: 'Set the title, link, passcode, cover, event date, and optional watermark.',
    detail: 'Turn off Public gallery link is active whenever clients should no longer open the gallery.',
    icon: SlidersHorizontal
  },
  {
    title: 'Collect client selections',
    eyebrow: 'Client Proofing',
    description: 'Enable favourites, choose a selection limit, and review each submitted selection.',
    detail: 'You can reopen a selection for changes or prepare its selected originals for download.',
    icon: Heart
  },
  {
    title: 'Preview, share, and learn',
    eyebrow: 'Ready for clients',
    description: 'Preview Gallery shows the client experience before you send the link.',
    detail: 'Use Share Gallery to copy or email the link, then Gallery Statistics to review engagement.',
    icon: Share2
  }
];

export function PhotographerOnboardingGuide({
  open,
  isPending,
  error,
  onDismiss
}: {
  open: boolean;
  isPending: boolean;
  error: Error | null;
  onDismiss: () => void;
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const step = GUIDE_STEPS[stepIndex];
  const StepIcon = step.icon;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === GUIDE_STEPS.length - 1;

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  function dismiss() {
    if (!isPending) onDismiss();
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) dismiss();
    }}>
      <DialogContent
        title="Welcome to Storyly"
        description="A quick guide to creating, preparing, and sharing your first client gallery."
        className="max-w-2xl"
      >
        <div className="grid gap-5">
          <div
            className="h-1.5 overflow-hidden rounded-full bg-ink/10"
            role="progressbar"
            aria-label="Onboarding progress"
            aria-valuemin={1}
            aria-valuemax={GUIDE_STEPS.length}
            aria-valuenow={stepIndex + 1}
          >
            <div
              className="h-full rounded-full bg-sea transition-[width] duration-300"
              style={{ width: `${((stepIndex + 1) / GUIDE_STEPS.length) * 100}%` }}
            />
          </div>

          <section className="grid min-h-64 content-center gap-5 rounded-sm border border-ink/10 bg-white/70 p-5 sm:grid-cols-[auto_1fr] sm:items-center sm:p-7">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-ink text-ivory sm:h-16 sm:w-16">
              <StepIcon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sea">{step.eyebrow}</p>
              <h2 className="mt-2 text-2xl font-semibold text-ink">{step.title}</h2>
              <p className="mt-3 text-sm leading-6 text-ink/75">{step.description}</p>
              <p className="mt-2 text-sm leading-6 text-ink/75">{step.detail}</p>
            </div>
          </section>

          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-ink/60" aria-live="polite">
              Step {stepIndex + 1} of {GUIDE_STEPS.length}
            </p>
            <div className="flex gap-1.5" aria-hidden="true">
              {GUIDE_STEPS.map((guideStep, index) => (
                <span
                  key={guideStep.title}
                  className={`h-1.5 rounded-full transition-all ${index === stepIndex ? 'w-6 bg-sea' : 'w-1.5 bg-ink/20'}`}
                />
              ))}
            </div>
          </div>

          {error && !isPending ? (
            <div role="alert" className="rounded-sm border border-clay/25 bg-clay/10 p-3 text-sm text-clay">
              Could not save your guide progress. Please try again.
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 border-t border-ink/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <Button type="button" variant="ghost" onClick={dismiss} disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {isPending ? 'Saving' : 'Skip guide'}
            </Button>
            <div className="flex justify-between gap-2 sm:justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStepIndex((current) => Math.max(0, current - 1))}
                disabled={isFirstStep || isPending}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Back
              </Button>
              {isLastStep ? (
                <Button type="button" onClick={dismiss} disabled={isPending}>
                  {isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <BookOpenCheck className="h-4 w-4" aria-hidden="true" />}
                  {isPending ? 'Saving' : 'Finish'}
                </Button>
              ) : (
                <Button type="button" onClick={() => setStepIndex((current) => Math.min(GUIDE_STEPS.length - 1, current + 1))} disabled={isPending}>
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
